// The single API client. Add an endpoint here or in `api/<resource>.ts` — never a raw fetch in
// a component. Type the generic at the call site: `request<ListResponse<Person>>('/persons')`.

const API_BASE = '/api';

/**
 * A refusal that kept its reasons. Some endpoints say no WITH STRUCTURE — a hold that collided
 * carries the colliding row — and the UI should render that, not just the flattened message.
 * Plain `instanceof Error` handling still works.
 */
export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!response.ok) {
    const parsed = await response.json().catch(() => ({ error: 'Request failed' }));
    const body = (parsed ?? {}) as Record<string, unknown>;
    throw new ApiError(response.status, body, (body as { error?: string }).error || `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Multipart upload (the multer routes) — no JSON content-type, the browser sets the boundary. */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include', body: form });
  if (!response.ok) {
    const parsed = await response.json().catch(() => ({ error: 'Upload failed' }));
    const body = (parsed ?? {}) as Record<string, unknown>;
    throw new ApiError(response.status, body, (body as { error?: string }).error || `Upload failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

/** `?q=&tier=` from an object, dropping every empty value. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
